import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import LibreAppWrapper from './LibreAppWrapper';

import { RootState } from 'src/reducers';
import { Partner } from 'src/reducers/partner/types';

import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

import { PARTNER_TYPES } from 'Constants/partnerTypes';

import PartnerListItem from 'Components/partner/PartnerListItem';

import backArrowImg from 'Images/back-arrow.svg';

import styles from './OtherConnections.module.scss';

const mapState = ({
  partner: { partners, connected, pendingConnections },
  user: { phoneLanguage },
}: RootState) => {
  return {
    connectedPartners: connected,
    language: phoneLanguage,
    partners,
    pendingConnections,
  };
};

const connector = connect(mapState);

const backToHome = () => {
  mediator.publish('router:redirect', '/');
};

// General invite page
const invitePage = () => {
  mediator.publish('router:redirect', '/invitation');
};

type Props = ConnectedProps<typeof connector>;

const OtherConnections: React.FC<React.PropsWithChildren<Props>> = ({
  connectedPartners,
  language,
  partners,
  pendingConnections,
}) => {
  const [running, setRunning] = useState('');

  // Setup an interval that checks every 200ms if a full second has passed, and if so unset the running ID
  // This will only ever happen when navigating back from somewhere else (i.e. a connected partner website)
  // Needed for managing the loading buttons on iOS
  useEffect(() => {
    let interval = 0;
    let prev = new Date().getTime();

    if (running) {
      interval = window.setInterval(() => {
        const now = new Date().getTime();
        const diff = now - prev - 200;

        if (diff > 1000) {
          setRunning('');
        }

        prev = now;
      }, 200);
    }

    return () => {
      window.clearInterval(interval);
    };
  }, [running]);

  const otherConnectionsApp = useMemo(() => {
    const appConfig = {
      id: 'otherConnections',
      logo: '',
      transGroupKey: 'OtherConnections',
    };

    return appConfig;
  }, []);

  const partnerValues = Object.values(partners) as Partner[];

  const [connected, pending, display] = useMemo(() => {
    const sortedOptions = Object.values(partnerValues)
      .reduce(
        (pv, cv) => {
          let isDisplay, isConnected, isPending;

          switch (cv.type) {
            case PARTNER_TYPES.API:
            case PARTNER_TYPES.INBOUND:
              isDisplay = cv.display;
            default:
              isConnected = connectedPartners.some((c) => c.clientId === cv.id);
              isPending = pendingConnections.some((c) => c.clientId === cv.id);
          }

          (isDisplay || isPending || isConnected) && pv[isConnected ? 0 : +!isPending + 1].push(cv);

          return pv;
        },
        [[], [], []] as Partner[][]
      )
      .map((v) => v.sort((a, b) => a.name.localeCompare(b.name, language.split('-')[0])));

    return sortedOptions;
  }, [connectedPartners, pendingConnections, language, partnerValues]);

  const getConnectAction = (p: Partner, isConnected: boolean, isPending: boolean) => {
    const shareConnectAction = `/share/${p.id}`;

    switch (p.type) {
      case PARTNER_TYPES.COMMERCIAL_API:
        if (isConnected) {
          return shareConnectAction;
        } else if (isPending) {
          return `/consent/manage/${p.id}`;
        } else {
          return '/invitation';
        }

      case PARTNER_TYPES.INBOUND:
        if (isConnected) {
          return shareConnectAction;
        } else {
          return `/inbound/${p.id}`;
        }

      case PARTNER_TYPES.INSULET:
      case PARTNER_TYPES.REDOX:
        return shareConnectAction;
      default:
        return isConnected ? shareConnectAction : p.connectUrl;
    }
  };

  const renderPartnerList = (s: Partner, isConnected: boolean, isPending: boolean) => (
    <PartnerListItem
      connectAction={getConnectAction(s, isConnected, isPending)}
      id={s.id}
      key={s.id}
      partnerData={s}
      running={running}
      setRunning={setRunning}
    />
  );

  return (
    <LibreAppWrapper app={otherConnectionsApp} appType="otherConnections">
      <div id="otherConnectionsList" className={styles.sharingList}>
        {(pending.length > 0 || display.length > 0) && (
          <div className={styles.sharingList}>
            <div className="pb-2 font-weight-bold">
              {i18n.t('OtherConnections.initiateConnections', { context: 'cap' })}
            </div>
            {pending.map((s) => renderPartnerList(s, false, true))}
            {display.map((s) => renderPartnerList(s, false, false))}
          </div>
        )}

        {connected.length > 0 && (
          <div className={styles.sharingList}>
            <div className="pt-2 pb-2 font-weight-bold">
              {i18n.t('Common.connections', { context: 'cap' })}
            </div>
            {connected.map((s) => renderPartnerList(s, true, false))}
          </div>
        )}

        <div className="pt-4">
          <button
            id="otherConnectionsBackButton"
            className="btn btn-lg btn-primary w-100"
            onClick={invitePage}
          >
            {i18n.t('Common.connectWitchCode', { context: 'cap' })}
          </button>
        </div>
      </div>

      <button
        id="otherConnectionsBackButton"
        className="btn btn-lg text-primary w-100 pt-4"
        onClick={backToHome}
      >
        <img className="rtlFlip mr-2" src={backArrowImg} />
        {i18n.t('Common.backToHome_cap', { context: 'cap' })}
      </button>
    </LibreAppWrapper>
  );
};

export default connector(OtherConnections);
